// Canonical, AES-focused country/region taxonomy used by older filters.
// "AES" = applies to all three Alliance of Sahel States; "ECOWAS" = the wider bloc.
export const COUNTRIES = ["AES", "Burkina Faso", "Mali", "Niger", "ECOWAS", "Other"];

// Kept for backward compatibility (older signup form). New code uses CountrySelect.
export const SIGNUP_COUNTRIES = [
  "Burkina Faso", "Mali", "Niger",
  "Bénin", "Cap-Vert", "Côte d'Ivoire", "Gambie", "Ghana", "Guinée",
  "Guinée-Bissau", "Liberia", "Nigeria", "Sénégal", "Sierra Leone", "Togo",
  "Other",
];

// Full ISO 3166-1 alpha-2 codes. Display names are localized at runtime via
// Intl.DisplayNames (FR/EN), so there's no hand-maintained name table to keep in sync.
// AES first, then the rest of ECOWAS, then everything else alphabetical-ish.
export const COUNTRY_CODES = [
  "BF", "ML", "NE", // AES
  "BJ", "CV", "CI", "GM", "GH", "GN", "GW", "LR", "NG", "SN", "SL", "TG", // rest of ECOWAS
  "DZ", "AO", "AR", "AM", "AU", "AT", "BS", "BH", "BD", "BB", "BY", "BE", "BZ", "BT", "BO",
  "BA", "BW", "BR", "BN", "BG", "BI", "KH", "CM", "CA", "CF", "TD", "CL", "CN", "CO", "KM",
  "CG", "CD", "CR", "HR", "CU", "CY", "CZ", "DK", "DJ", "DM", "DO", "EC", "EG", "SV", "GQ",
  "ER", "EE", "SZ", "ET", "FJ", "FI", "FR", "GA", "GE", "DE", "GR", "GD", "GT", "GY", "HT",
  "HN", "HU", "IS", "IN", "ID", "IR", "IQ", "IE", "IL", "IT", "JM", "JP", "JO", "KZ", "KE",
  "KI", "KW", "KG", "LA", "LV", "LB", "LS", "LY", "LI", "LT", "LU", "MG", "MW", "MY", "MV",
  "MT", "MH", "MR", "MU", "MX", "FM", "MD", "MC", "MN", "ME", "MA", "MZ", "MM", "NA", "NR",
  "NP", "NL", "NZ", "NI", "KP", "MK", "NO", "OM", "PK", "PW", "PS", "PA", "PG", "PY", "PE",
  "PH", "PL", "PT", "QA", "RO", "RU", "RW", "KN", "LC", "VC", "WS", "SM", "ST", "SA", "RS",
  "SC", "SG", "SK", "SI", "SB", "SO", "ZA", "KR", "SS", "ES", "LK", "SD", "SR", "SE", "CH",
  "SY", "TW", "TJ", "TZ", "TH", "TL", "TO", "TT", "TN", "TR", "TM", "TV", "UG", "UA", "AE",
  "GB", "US", "UY", "UZ", "VU", "VE", "VN", "YE", "ZM", "ZW",
];

const _dnCache = {};
function _dn(lang) {
  if (!_dnCache[lang]) {
    try { _dnCache[lang] = new Intl.DisplayNames([lang], { type: "region" }); }
    catch { _dnCache[lang] = null; }
  }
  return _dnCache[lang];
}

// Localized name for an ISO code (e.g. "BF" -> "Burkina Faso").
export function nameForCode(code, lang = "en") {
  const dn = _dn(lang);
  try { return (dn && dn.of(code)) || code; } catch { return code; }
}

// ---- Grouping: AES first, then the rest of Africa, then the rest of the world ----
export const AES_CODES = ["BF", "ML", "NE"];

// All 54 African countries (AES included).
export const AFRICA_CODES = [
  "DZ", "AO", "BJ", "BW", "BF", "BI", "CV", "CM", "CF", "TD", "KM", "CG", "CD", "CI",
  "DJ", "EG", "GQ", "ER", "SZ", "ET", "GA", "GM", "GH", "GN", "GW", "KE", "LS", "LR",
  "LY", "MG", "MW", "ML", "MR", "MU", "MA", "MZ", "NA", "NE", "NG", "RW", "ST", "SN",
  "SC", "SL", "SO", "ZA", "SS", "SD", "TZ", "TG", "TN", "UG", "ZM", "ZW",
];

const _AES_SET = new Set(AES_CODES);
const _AFRICA_SET = new Set(AFRICA_CODES);

// Display order of the three groups.
export const COUNTRY_GROUP_ORDER = ["aes", "africa", "world"];
const _GROUP_RANK = { aes: 0, africa: 1, world: 2 };

export function groupForCode(code) {
  if (_AES_SET.has(code)) return "aes";
  if (_AFRICA_SET.has(code)) return "africa";
  return "world";
}

// Reverse lookup: canonical EN name -> ISO code (built once).
let _codeByName = null;
function codeForName(value) {
  if (!_codeByName) {
    _codeByName = {};
    for (const c of COUNTRY_CODES) {
      const n = nameForCode(c, "en");
      if (n && n !== c) _codeByName[n] = c;
    }
  }
  return _codeByName[value] || null;
}

// Group of an already-stored country value (canonical EN name).
export function groupForValue(value) {
  const c = codeForName(value);
  return c ? groupForCode(c) : "world";
}

// Build options list: { code, value (canonical EN name), label (localized), group },
// ordered AES -> other African countries -> rest of the world, alphabetical within each.
export function countryOptions(lang = "en") {
  return COUNTRY_CODES
    .map((c) => ({ code: c, value: nameForCode(c, "en"), label: nameForCode(c, lang), group: groupForCode(c) }))
    .filter((o) => o.value && o.value !== o.code)
    .sort((a, b) => (_GROUP_RANK[a.group] - _GROUP_RANK[b.group]) || a.label.localeCompare(b.label, lang));
}

// Split a flat option list into the three ordered groups (empty groups dropped).
export function groupCountryOptions(options) {
  return COUNTRY_GROUP_ORDER
    .map((key) => ({ key, options: options.filter((o) => (o.group || "world") === key) }))
    .filter((g) => g.options.length > 0);
}

// Localize an already-stored country value (canonical EN name, or a legacy raw string).
export function countryLabel(value, lang = "en") {
  if (!value) return "";
  const c = codeForName(value);
  return c ? nameForCode(c, lang) : value; // unknown/legacy → show as-is
}
